import React from 'react'
import { useTranslation } from 'react-i18next'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import { Button } from '@pandiafi/design-system'
import { useLocation, useNavigate } from 'react-router-dom'

import { ResidentAppError } from '../../common/error'
import { ResidentAppErrorCode } from '../../common/types'
import { ErrorPageNavbar, Footer, Error } from '../components'
import { resolveResidentAppErrorMessage } from '../utils/resolveResidentAppErrorMessage'
import { isFeatureAccessDenied } from '../utils/isFeatureAccessDenied'

import { ErrorPage } from 'views/ErrorPage'
import routePath from 'router/routePath'
import { useReturnToPreviousView } from 'hooks/useReturnToPreviousView/useReturnToPreviousView'

interface ErrorHandlerProps {
  children: React.ReactNode
}

export const ErrorHandler: React.FC<ErrorHandlerProps> = ({ children }) => {
  const { t } = useTranslation(['errors', 'common'])
  const navigate = useNavigate()
  const location = useLocation()
  const returnToPreviousView = useReturnToPreviousView()

  const fallbackRender = ({ error, resetErrorBoundary }: FallbackProps) => {
    const resolved = resolveResidentAppErrorMessage(error as Error, t)
    const isWriteAccessDenied =
      error instanceof ResidentAppError &&
      (error.errorCode ===
        ResidentAppErrorCode.IMPERSONATION_WRITE_ACCESS_DENIED ||
        error.errorCode === ResidentAppErrorCode.MOVED_OUT_WRITE_ACCESS_DENIED)

    const isAccessDenied = isFeatureAccessDenied(error)

    const resolveActionOverride = () => {
      if (isWriteAccessDenied) {
        return { onClick: () => navigate(-1), buttonText: t('common:back') }
      }
      if (isAccessDenied) {
        return { onClick: returnToPreviousView, buttonText: t('common:back') }
      }
      return undefined
    }

    const actionOverride = resolveActionOverride()

    return (
      <>
        <ErrorPageNavbar />
        <ErrorPage
          message={resolved.message}
          illustration={resolved.illustration}
          eventId={resolved.eventId}
          action={
            (isAccessDenied || location.pathname !== routePath.root) && (
              <Button
                onClick={() => {
                  resetErrorBoundary()
                  if (actionOverride?.onClick) {
                    actionOverride.onClick()
                  } else if (resolved.reloadButtonText) {
                    window.location.reload()
                  } else {
                    navigate(routePath.root)
                  }
                }}
              >
                {actionOverride?.buttonText ??
                  resolved.reloadButtonText ??
                  t('other.backToLogin')}
              </Button>
            )
          }
        />
        <Footer layout="public" />
      </>
    )
  }

  return (
    <ErrorBoundary
      fallbackRender={fallbackRender}
      resetKeys={[location.pathname]}
    >
      {children}
    </ErrorBoundary>
  )
}
